import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfo } from 'types';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setIDsSubcategory: (value: string[]) => void;
	index: number;
	IDSelectedCategory: Nullable<string>;
	IDsSelectedSubcategories: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({
		data,
		isLoading,
		refetch,
		responseError,
		setIDsSubcategory,
		index,
		IDSelectedCategory,
		IDsSelectedSubcategories,
	}) => {
		const [searchString, setSearchString] = useState<string>('');
		const { t } = useTranslation('error');
		const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectSubcategory') }], [t]);

		const filteredData = useMemo(() => {
			return searchString
				? data.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: data;
		}, [searchString, data]);

		const handleSelectAllData = useCallback(() => {
			if (data.length === IDsSelectedSubcategories.length) {
				setIDsSubcategory([]);
			} else {
				const IDsOfAllSubcategories = data.map((el) => el.id);
				setIDsSubcategory(IDsOfAllSubcategories);
			}
		}, [data, IDsSelectedSubcategories, setIDsSubcategory]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={!!(data.length && data.length === IDsSelectedSubcategories.length)}
								disabled={!data.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllSubcategoriesLabel"
									asFragment={true}
								/>
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[data, IDsSelectedSubcategories, handleSelectAllData, searchString, setSearchString],
		);

		useEffect(() => {
			setSearchString('');
		}, [IDSelectedCategory]);

		return (
			<FormSelect
				testId="MultipleSelect"
				name={`IDsSelectedSubcategories-${index}`}
				label="subcategoriesLabel"
				options={filteredData}
				validationRules={validationRules}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={!IDSelectedCategory || isLoading}
				onChange={setIDsSubcategory}
				mode="multiple"
				counterValue={IDsSelectedSubcategories.length}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
